import { createFileRoute, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Menu, MessageSquare } from 'lucide-react';

import { Button } from '../../../../components/ui';
import { useQueueManager } from '../../../../hooks/useQueueManager';
import { ClubChannelSidebar } from '../../../../components/club/ClubChannelSidebar';
import { ClubChatView } from '../../../../components/club/ClubChatView';
import { ClubHubSidebar } from '../../../../components/club/ClubHubSidebar';
import { trpc } from '../../../../lib/trpc';

import type { SelectedItem } from '../../../../components/club/ClubChannelSidebar';

export const Route = createFileRoute('/app/clubs/$slug')({
  component: ClubSlugLayout,
});

function ClubSlugLayout() {
  const { slug } = Route.useParams();
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const isMember = !!membership;

  const matchRoute = useMatchRoute();
  const isSettings = !!matchRoute({ to: '/app/clubs/$slug/settings', params: { slug } });
  const isQueuesSubroute = !!matchRoute({ to: '/app/clubs/$slug/queues', params: { slug }, fuzzy: true });
  const isSubroute = isSettings || isQueuesSubroute;

  const { setActiveClub } = useQueueManager();
  const [selected, setSelected] = useState<SelectedItem>({ type: 'home' });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Set active club for navbar queue manager
  useEffect(() => {
    if (membership?.id) {
      setActiveClub(membership.id, slug);
    }
  }, [membership?.id, slug, setActiveClub]);

  // Set page title to club name
  useEffect(() => {
    const clubName = membership?.name ?? slug;
    document.title = `${clubName} - Rivaled`;
    return () => { document.title = 'Rivaled'; };
  }, [membership?.name, slug]);

  // Fetch real channels for this club
  const { data: channels } = trpc.chat.listChannels.useQuery(
    { clubId: membership?.id },
    { enabled: isMember && !!membership?.id },
  );

  // Fetch DM conversations
  const { data: dmData } = trpc.dm.listConversations.useQuery(
    undefined,
    { enabled: isMember },
  );

  const utils = trpc.useUtils();

  // Auto-select first channel when channels load and nothing is selected
  useEffect(() => {
    if (selected.type === 'home' && channels && channels.length > 0) {
      setSelected({ type: 'channel', id: channels[0].id });
    }
  }, [channels, selected.type]);

  const showThreeColumnLayout = isMember && !isSubroute;

  // For subroutes (settings, queue detail), show channel sidebar + outlet
  if (isMember && isSubroute) {
    return (
      <div className="flex h-[calc(100vh-4rem)] w-full">
        <ChannelSidebarWrapper
          slug={slug}
          membership={membership!}
          channels={channels}
          dmData={dmData}
          selected={selected}
          setSelected={setSelected}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          utils={utils}
        />
        <div className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    );
  }

  // Non-members: no channel sidebar
  if (!showThreeColumnLayout) {
    return <Outlet />;
  }

  // Map DB channels to the format ClubChannelSidebar expects
  const mappedChannels = (channels ?? []).map((ch) => ({
    id: ch.id,
    name: ch.name,
    type: ch.type as 'general' | 'lfg' | 'custom',
    isReadOnly: ch.isReadOnly,
    slowModeSeconds: ch.slowModeSeconds || undefined,
  }));

  // Map DM conversations for sidebar
  const mappedDms = (dmData?.items ?? []).map((conv) => ({
    id: conv.id,
    username: conv.otherUser.username,
    avatarUrl: conv.otherUser.avatarUrl,
    unreadCount: 0,
  }));

  // Build a lookup for DM other user info
  const dmLookup = new Map(
    (dmData?.items ?? []).map((conv) => [conv.id, conv.otherUser]),
  );

  const userRole = (membership?.role as 'owner' | 'admin' | 'mod' | 'member') ?? 'member';

  const handleSelectDm = (conversationId: string) => {
    setSelected({ type: 'dm', id: conversationId });
    utils.dm.listConversations.invalidate();
  };

  // Resolve what chat view to show
  const chatView = (() => {
    if (selected.type === 'channel') {
      const channel = mappedChannels.find((c) => c.id === selected.id);
      if (!channel) return null;
      return (
        <ClubChatView
          variant={{
            type: 'channel',
            channel,
            channelId: selected.id,
            clubId: membership!.id,
          }}
          userRole={userRole}
          onSelectDm={handleSelectDm}
          showMemberList
          onMobileMenuOpen={() => setMobileOpen(true)}
        />
      );
    }
    if (selected.type === 'dm') {
      const otherUser = dmLookup.get(selected.id);
      if (!otherUser) return null;
      return (
        <ClubChatView
          variant={{
            type: 'dm',
            conversationId: selected.id,
            otherUser,
          }}
          userRole={userRole}
          onSelectDm={handleSelectDm}
          onMobileMenuOpen={() => setMobileOpen(true)}
        />
      );
    }
    return null;
  })();

  return (
    <div className="flex h-[calc(100vh-4rem)] w-full">
      {/* Left: Channel sidebar */}
      <ClubChannelSidebar
        clubName={membership?.name ?? slug}
        clubId={membership!.id}
        clubAvatarUrl={membership?.avatarUrl}
        clubBannerUrl={membership?.bannerUrl}
        clubSlug={slug}
        channels={mappedChannels}
        dmConversations={mappedDms}
        onNewDm={handleSelectDm}
        partyChat={null}
        selected={selected}
        onSelect={(item) => { setSelected(item); setMobileOpen(false); }}
        userRole={userRole}
        isMobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Center: Chat (main content) — visible on all sizes */}
      <div className="flex flex-col flex-1 min-w-0 h-full">
        {chatView ?? (
          <div className="flex-1 flex items-center justify-center text-surface-500 text-sm">
            <div className="text-center">
              <MessageSquare className="size-8 mx-auto mb-2 text-surface-600" />
              <p>Select a channel to start chatting</p>
            </div>
          </div>
        )}
      </div>

      {/* Right: Hub sidebar */}
      <div className="hidden xl:flex w-80 2xl:w-96 shrink-0 border-l border-surface-700/50 bg-surface-800">
        <ClubHubSidebar clubId={membership!.id} slug={slug} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Channel sidebar wrapper for subroutes
// ---------------------------------------------------------------------------

function ChannelSidebarWrapper({
  slug,
  membership,
  channels,
  dmData,
  selected,
  setSelected,
  mobileOpen,
  setMobileOpen,
  utils,
}: {
  slug: string;
  membership: { id: string; name: string; role: string; avatarUrl?: string | null; bannerUrl?: string | null };
  channels: { id: string; name: string; type: string; isReadOnly: boolean; slowModeSeconds: number | null }[] | undefined;
  dmData: { items: { id: string; otherUser: { id: string; username: string; avatarUrl?: string | null } }[] } | undefined;
  selected: SelectedItem;
  setSelected: (item: SelectedItem) => void;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  utils: ReturnType<typeof trpc.useUtils>;
}) {
  const mappedChannels = (channels ?? []).map((ch) => ({
    id: ch.id,
    name: ch.name,
    type: ch.type as 'general' | 'lfg' | 'custom',
    isReadOnly: ch.isReadOnly,
    slowModeSeconds: ch.slowModeSeconds || undefined,
  }));

  const mappedDms = (dmData?.items ?? []).map((conv) => ({
    id: conv.id,
    username: conv.otherUser.username,
    avatarUrl: conv.otherUser.avatarUrl,
    unreadCount: 0,
  }));

  const userRole = (membership.role as 'owner' | 'admin' | 'mod' | 'member') ?? 'member';

  return (
    <>
      <Button
        variant="secondary"
        size="icon-md"
        className="fixed bottom-4 left-4 z-40 md:hidden shadow-lg"
        onClick={() => setMobileOpen(true)}
      >
        <Menu className="size-5" />
      </Button>

      <ClubChannelSidebar
        clubName={membership.name ?? slug}
        clubId={membership.id}
        clubAvatarUrl={membership.avatarUrl}
        clubBannerUrl={membership.bannerUrl}
        clubSlug={slug}
        channels={mappedChannels}
        dmConversations={mappedDms}
        onNewDm={(id) => {
          setSelected({ type: 'dm', id });
          utils.dm.listConversations.invalidate();
        }}
        partyChat={null}
        selected={selected}
        onSelect={(item) => { setSelected(item); setMobileOpen(false); }}
        userRole={userRole}
        isMobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
    </>
  );
}
